import React, { useState } from "react";
import { JOBS, CANDIDATE_APPS, CANDIDATES, TRACKER_STAGES } from "./data";
import { AvailBadge, Chip, Icon, MatchRing, Progress, Logo, Avatar, CompanyLogo } from "./ui";

const me = CANDIDATES[0]; // Arjun

export default function CandidateApp({ onExit, hideNav }) {
  const [screen, setScreen] = useState("splash");
  const [tab, setTab] = useState("home");
  const [selectedJob, setSelectedJob] = useState(JOBS[0]);
  const [applied, setApplied] = useState(new Set(CANDIDATE_APPS.map(a => a.jobId)));
  const [saved, setSaved] = useState(new Set(["j2"]));
  const [otp, setOtp] = useState("");
  const [avail, setAvail] = useState({ status: "notice", days: 35 });
  const [showNav, setShowNav] = useState(!hideNav);

  const go = (s) => setScreen(s);

  return (
    <div className="min-h-screen w-full grain relative" style={{background: "linear-gradient(135deg, #F1EDE3 0%, #E8E0CE 100%)"}}>
      <div className="max-w-6xl mx-auto py-6 px-4 md:py-8 md:px-6 relative z-10">
        <header className="flex items-center justify-between mb-6 md:mb-8">
          {hideNav ? <span/> : (
            <button data-testid="candidate-exit-btn" onClick={onExit} className="btn btn-ghost text-sm">
              <Icon.ChevronLeft /> <span className="hidden sm:inline">All prototypes</span>
            </button>
          )}
          <Logo small/>
          <div className="text-xs text-[var(--muted)] font-mono hidden md:block">Candidate · Mobile app</div>
          {!hideNav && (
            <button onClick={() => setShowNav(!showNav)} className="btn btn-ghost text-sm lg:hidden">
              {showNav ? "Hide" : "Screens"} ({SCREENS.length})
            </button>
          )}
          {hideNav && <span className="w-10"/>}
        </header>

        <div className={`flex flex-col ${hideNav ? "items-center" : "lg:flex-row"} gap-6 lg:gap-10 items-start`}>
          {/* Phone */}
          <div className="mx-auto lg:mx-0 flex-shrink-0 phone-wrapper">
            <div className="phone-frame">
              <div className="phone-notch"/>
              <div className="phone-screen">
                <div className="phone-status">
                  <span>9:41</span>
                  <span className="flex gap-1 items-center text-xs">●●●● 5G ▮▮▮</span>
                </div>
                <div className="phone-content">
                  {screen === "splash" && <SplashScreen go={go}/>}
                  {screen === "login" && <LoginScreen go={go}/>}
                  {screen === "otp" && <OtpScreen otp={otp} setOtp={setOtp} go={go}/>}
                  {screen === "availability" && <AvailabilityScreen avail={avail} setAvail={setAvail} go={go}/>}
                  {screen === "profile-build" && <ProfileBuildScreen go={go}/>}
                  {screen === "app" && (
                    <>
                      {tab === "home" && <HomeFeed avail={avail} onOpenJob={(j) => { setSelectedJob(j); go("job-detail"); }} saved={saved} setSaved={setSaved}/>}
                      {tab === "jobs" && <JobsSearch onOpenJob={(j) => { setSelectedJob(j); go("job-detail"); }}/>}
                      {tab === "apps" && <AppsTracker/>}
                      {tab === "profile" && <ProfileScreen avail={avail}/>}
                    </>
                  )}
                  {screen === "job-detail" && (
                    <JobDetail job={selectedJob} onBack={() => go("app")} onApply={() => go("apply")} saved={saved} setSaved={setSaved}/>
                  )}
                  {screen === "apply" && <ApplyScreen job={selectedJob} onDone={() => {
                    setApplied(new Set([...applied, selectedJob.id])); go("apply-success");
                  }} onBack={() => go("job-detail")}/>}
                  {screen === "apply-success" && <ApplySuccess job={selectedJob} go={go} setTab={setTab}/>}
                </div>
                {screen === "app" && (
                  <div className="phone-tabbar">
                    <button data-testid="tab-home" className={tab==="home"?"active":""} onClick={() => setTab("home")}><Icon.Home/> Home</button>
                    <button data-testid="tab-jobs" className={tab==="jobs"?"active":""} onClick={() => setTab("jobs")}><Icon.Search/> Jobs</button>
                    <button data-testid="tab-apps" className={tab==="apps"?"active":""} onClick={() => setTab("apps")}><Icon.Briefcase/> Applied</button>
                    <button data-testid="tab-profile" className={tab==="profile"?"active":""} onClick={() => setTab("profile")}><Icon.User/> Profile</button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Screen navigator */}
          <aside className={`flex-1 max-w-md w-full ${hideNav ? "hidden" : (showNav ? "block lg:block" : "hidden lg:block")}`}>
            <p className="text-xs uppercase tracking-widest text-[var(--muted)] mb-3">Prototype screens</p>
            <h2 className="font-display text-3xl mb-1">Candidate onboarding & job flow</h2>
            <p className="text-sm text-[var(--muted)] mb-6">Click any screen to jump into that state. All 12 screens are wired.</p>
            <div className="space-y-2">
              {SCREENS.map(s => {
                const isActive = (s.id === "home" && screen === "app") ? tab === s.tab : screen === s.id;
                return (
                <button
                  key={`${s.n}-${s.id}`}
                  data-testid={`nav-${s.tab || s.id}`}
                  onClick={() => { setScreen(s.id === "home" ? "app" : s.id); if (s.tab) setTab(s.tab); }}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition ${
                    isActive ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "bg-white border-[var(--line)] hover:bg-[var(--paper-2)]"
                  }`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">{s.n}. {s.name}</span>
                    <Icon.Arrow/>
                  </div>
                </button>
                );
              })}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

const SCREENS = [
  { id: "splash", n: "01", name: "Splash / welcome" },
  { id: "login", n: "02", name: "Sign in options" },
  { id: "otp", n: "03", name: "OTP verification" },
  { id: "availability", n: "04", name: "Declare availability" },
  { id: "profile-build", n: "05", name: "Profile builder" },
  { id: "home", n: "06", name: "Home feed (matched)", tab: "home" },
  { id: "home", n: "07", name: "Job search + filters", tab: "jobs" },
  { id: "job-detail", n: "08", name: "Job detail" },
  { id: "apply", n: "09", name: "Apply — screening" },
  { id: "apply-success", n: "10", name: "Application submitted" },
  { id: "home", n: "11", name: "Applications tracker", tab: "apps" },
  { id: "home", n: "12", name: "Candidate profile", tab: "profile" },
];

// -------------------- Screens --------------------
function SplashScreen({ go }) {
  return (
    <div className="h-full flex flex-col justify-between px-6 pt-12 pb-6" style={{background: "linear-gradient(180deg, #0F3D2E 0%, #082018 100%)", color: "white"}}>
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-[var(--brand-2)] grid place-items-center font-display font-bold">tx</div>
      </div>
      <div className="fade-up">
        <h1 className="font-display text-4xl leading-[1.05] font-semibold">Land your next role<br/><span className="text-[var(--brand-2)]">before your notice ends.</span></h1>
        <p className="mt-4 text-sm text-white/70">The urgent-hiring app for people on notice period or ready to join immediately.</p>
      </div>
      <div className="space-y-3">
        <button data-testid="splash-get-started" className="btn btn-accent w-full" onClick={() => go("login")}>Get started</button>
        <button data-testid="splash-signin" className="btn w-full" style={{color:"white", borderColor:"rgba(255,255,255,0.2)", background:"transparent", border:"1px solid rgba(255,255,255,0.2)"}} onClick={() => go("login")}>I already have an account</button>
      </div>
    </div>
  );
}

function LoginScreen({ go }) {
  return (
    <div className="px-6 pt-10 pb-6 h-full flex flex-col">
      <h1 className="font-display text-3xl leading-tight">Welcome to<br/>TalentXpres</h1>
      <p className="text-sm text-[var(--muted)] mt-2">Sign in to keep your career moving.</p>
      <div className="mt-8 space-y-3">
        <button data-testid="login-google" className="btn btn-ghost w-full justify-start px-4">
          <span className="w-5 h-5 rounded bg-white border grid place-items-center text-xs font-bold text-red-500">G</span>
          Continue with Google
        </button>
        <button data-testid="login-mobile" className="btn btn-ghost w-full justify-start px-4" onClick={() => go("otp")}>
          <Icon.User/> Continue with Mobile OTP
        </button>
      </div>
      <div className="my-6 flex items-center gap-3 text-xs text-[var(--muted)]"><span className="flex-1 h-px bg-[var(--line)]"/> or use email <span className="flex-1 h-px bg-[var(--line)]"/></div>
      <div className="space-y-3">
        <input className="w-full px-4 py-3 rounded-xl border border-[var(--line)] bg-white text-sm" placeholder="you@company.com" defaultValue="arjun@example.com"/>
        <input className="w-full px-4 py-3 rounded-xl border border-[var(--line)] bg-white text-sm" placeholder="Password" type="password" defaultValue="••••••••"/>
        <button data-testid="login-submit" className="btn btn-primary w-full" onClick={() => go("availability")}>Sign in</button>
      </div>
      <p className="mt-auto text-xs text-center text-[var(--muted)]">By continuing you agree to our T&Cs and Privacy Policy</p>
    </div>
  );
}

function OtpScreen({ otp, setOtp, go }) {
  return (
    <div className="px-6 pt-10 h-full flex flex-col">
      <button className="btn btn-ghost text-sm w-fit mb-4" onClick={() => go("login")}><Icon.ChevronLeft/> Back</button>
      <h1 className="font-display text-3xl">Verify your mobile</h1>
      <p className="text-sm text-[var(--muted)] mt-2">Sent to +91 98123 45001. Use code <b>123456</b> in demo.</p>
      <div className="flex gap-2 mt-8">
        {[0,1,2,3,4,5].map(i => (
          <input key={i} value={otp[i] || ""} onChange={(e) => {
            const v = e.target.value.slice(-1);
            const next = (otp + " ".repeat(6)).split("").slice(0,6);
            next[i] = v; setOtp(next.join("").trim());
          }} className="flex-1 aspect-square rounded-xl border border-[var(--line)] bg-white text-center font-display text-2xl"/>
        ))}
      </div>
      <button data-testid="otp-verify" className="btn btn-primary w-full mt-8" onClick={() => go("availability")}>Verify & continue</button>
      <p className="text-xs text-center text-[var(--muted)] mt-4">Resend in 0:28</p>
    </div>
  );
}

function AvailabilityScreen({ avail, setAvail, go }) {
  return (
    <div className="px-6 pt-8 h-full flex flex-col">
      <p className="text-xs text-[var(--muted)] font-mono">STEP 1 OF 3</p>
      <h1 className="font-display text-2xl mt-2 leading-tight">When can you start<br/>your next role?</h1>
      <p className="text-sm text-[var(--muted)] mt-2">This unlocks matches from employers with urgent openings.</p>

      <div className="mt-6 space-y-3">
        <button data-testid="avail-immediate" onClick={() => setAvail({ status: "immediate", days: 0 })}
          className={`w-full text-left p-4 rounded-2xl border transition ${avail.status === "immediate" ? "border-[var(--brand-2)] bg-[var(--brand-3)]/50" : "border-[var(--line)] bg-white"}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="font-semibold">{"I'm an immediate joiner"}</div>
              <div className="text-xs text-[var(--muted)] mt-1">Available to start now</div>
            </div>
            {avail.status === "immediate" && <div className="w-6 h-6 rounded-full bg-[var(--brand-2)] grid place-items-center"><Icon.Check color="white"/></div>}
          </div>
        </button>
        <button data-testid="avail-notice" onClick={() => setAvail({ status: "notice", days: 35 })}
          className={`w-full text-left p-4 rounded-2xl border transition ${avail.status === "notice" ? "border-[var(--brand-2)] bg-[var(--brand-3)]/50" : "border-[var(--line)] bg-white"}`}>
          <div className="flex justify-between items-start">
            <div>
              <div className="font-semibold">{"I'm serving notice period"}</div>
              <div className="text-xs text-[var(--muted)] mt-1">Currently employed</div>
            </div>
            {avail.status === "notice" && <div className="w-6 h-6 rounded-full bg-[var(--brand-2)] grid place-items-center"><Icon.Check color="white"/></div>}
          </div>
        </button>
      </div>

      {avail.status === "notice" && (
        <div className="mt-4 fade-up">
          <label className="text-xs text-[var(--muted)]">Notice ends in</label>
          <div className="mt-2 flex gap-2 flex-wrap">
            {[15, 30, 45, 60, 90].map(d => (
              <button key={d} onClick={() => setAvail({...avail, days: d})}
                className={`px-4 py-2 rounded-full text-sm font-medium border ${avail.days === d ? "bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]" : "bg-white border-[var(--line)]"}`}>
                {d} days
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-auto pb-2 pt-6">
        <div className="p-3 rounded-xl bg-[var(--brand-3)]/40 text-xs text-[var(--brand)]">
          <b>The TalentXpres Rule:</b> we only match you with employers whose hiring timeline aligns with your availability.
        </div>
        <button data-testid="avail-continue" onClick={() => go("profile-build")} className="btn btn-primary w-full mt-4">Continue</button>
      </div>
    </div>
  );
}

function ProfileBuildScreen({ go }) {
  return (
    <div className="px-6 pt-8 h-full flex flex-col">
      <p className="text-xs text-[var(--muted)] font-mono">STEP 2 OF 3</p>
      <h1 className="font-display text-2xl mt-2">Tell us about yourself</h1>
      <div className="mt-4"><Progress value={45}/></div>
      <p className="text-xs text-[var(--muted)] mt-1">Profile 45% complete · Reach 70% to apply</p>

      <div className="mt-6 space-y-3 overflow-y-auto">
        <Input label="Current title" value="Senior Backend Engineer"/>
        <Input label="Current company" value="PayLite"/>
        <div className="grid grid-cols-2 gap-3">
          <Input label="Experience" value="5 years"/>
          <Input label="Location" value="Bangalore"/>
        </div>
        <div>
          <label className="text-xs text-[var(--muted)]">Core skills</label>
          <div className="flex flex-wrap gap-2 mt-2">
            {["Python", "FastAPI", "PostgreSQL", "AWS", "Docker"].map(s => <Chip key={s} tone="mint">{s}</Chip>)}
            <button className="px-2.5 py-1 rounded-full text-xs border border-dashed border-[var(--line)] text-[var(--muted)]">+ Add</button>
          </div>
        </div>
        <div className="p-3 border border-dashed border-[var(--line)] rounded-xl bg-white text-center">
          <div className="text-sm font-medium">Upload resume</div>
          <div className="text-xs text-[var(--muted)] mt-1">PDF, DOCX · max 5MB</div>
          <button className="btn btn-ghost mt-2 text-xs px-4 py-1.5">Choose file</button>
        </div>
      </div>

      <button data-testid="profile-continue" className="btn btn-primary w-full mt-4" onClick={() => go("app")}>Finish & explore jobs</button>
    </div>
  );
}

function Input({ label, value }) {
  return (
    <div>
      <label className="text-xs text-[var(--muted)]">{label}</label>
      <input defaultValue={value} className="mt-1 w-full px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white text-sm"/>
    </div>
  );
}

// ---------------- Main app screens ----------------

function HomeFeed({ avail, onOpenJob, saved, setSaved }) {
  const daysLeft = avail.status === "immediate" ? 0 : avail.days;
  const top = JOBS.filter(j => j.match >= 80);
  const good = JOBS.filter(j => j.match < 80);
  return (
    <div className="px-5 pt-4 pb-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs text-[var(--muted)]">Good morning,</div>
          <div className="font-display text-xl">Arjun 👋</div>
        </div>
        <div className="relative"><Icon.Bell/><span className="absolute -top-1 -right-1 w-2 h-2 bg-[var(--accent)] rounded-full"/></div>
      </div>

      <div className="mt-4 p-4 rounded-2xl relative overflow-hidden" style={{background: "linear-gradient(135deg, #0F3D2E 0%, #14B87A 100%)", color: "white"}}>
        <div className="text-xs opacity-80">Your availability</div>
        <div className="font-display text-2xl mt-1">
          {avail.status === "immediate" ? "Immediate joiner" : `${daysLeft} days to join`}
        </div>
        <div className="text-xs opacity-80 mt-1">Matched with {JOBS.filter(j => j.maxJoiningDays >= daysLeft).length} urgent openings</div>
        <div className="absolute right-3 bottom-3 opacity-30 font-display text-6xl leading-none">{daysLeft || "0"}</div>
      </div>

      <div className="mt-6 flex items-baseline justify-between">
        <h3 className="font-display text-lg">Top matches</h3>
        <span className="text-xs text-[var(--muted)]">{top.length} jobs</span>
      </div>
      <div className="mt-3 space-y-3">
        {top.map(j => <JobCard key={j.id} job={j} onOpen={onOpenJob} saved={saved.has(j.id)} onSave={() => {
          const n = new Set(saved); n.has(j.id) ? n.delete(j.id) : n.add(j.id); setSaved(n);
        }}/>)}
      </div>

      <div className="mt-6 flex items-baseline justify-between">
        <h3 className="font-display text-lg">Good matches</h3>
      </div>
      <div className="mt-3 space-y-3">
        {good.map(j => <JobCard key={j.id} job={j} onOpen={onOpenJob} saved={saved.has(j.id)} onSave={() => {
          const n = new Set(saved); n.has(j.id) ? n.delete(j.id) : n.add(j.id); setSaved(n);
        }}/>)}
      </div>
    </div>
  );
}

function JobCard({ job, onOpen, saved, onSave }) {
  return (
    <div className="card p-4 card-lift cursor-pointer" onClick={() => onOpen(job)} data-testid={`job-card-${job.id}`}>
      <div className="flex items-start gap-3">
        <CompanyLogo name={job.company} size={44}/>
        <div className="flex-1">
          <div className="font-semibold leading-snug">{job.title}</div>
          <div className="text-xs text-[var(--muted)] mt-0.5">{job.company}</div>
          <div className="flex gap-1.5 mt-2 flex-wrap">
            <Chip><Icon.Location/> {job.location}</Chip>
            <Chip><Icon.Money/> ₹{job.salaryMin}-{job.salaryMax}L</Chip>
            <Chip tone="accent"><Icon.Clock/> ≤{job.maxJoiningDays}d joining</Chip>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <MatchRing score={job.match} size={40}/>
          <button onClick={(e) => { e.stopPropagation(); onSave(); }} className={saved ? "text-[var(--brand)]" : "text-[var(--muted)]"}><Icon.Bookmark fill={saved ? "currentColor" : "none"}/></button>
        </div>
      </div>
    </div>
  );
}

function JobsSearch({ onOpenJob }) {
  const [q, setQ] = useState("");
  const filtered = JOBS.filter(j => !q || j.title.toLowerCase().includes(q.toLowerCase()) || j.skills.some(s => s.toLowerCase().includes(q.toLowerCase())));
  return (
    <div className="px-5 pt-4">
      <h1 className="font-display text-2xl">Search jobs</h1>
      <div className="mt-3 flex gap-2">
        <div className="flex-1 flex items-center gap-2 px-3 py-2.5 rounded-xl border border-[var(--line)] bg-white">
          <Icon.Search/><input value={q} onChange={e => setQ(e.target.value)} placeholder="Skills, title, company" className="flex-1 bg-transparent outline-none text-sm"/>
        </div>
        <button className="btn btn-ghost !p-3"><Icon.Filter/></button>
      </div>
      <div className="mt-4 flex gap-2 overflow-x-auto pb-2">
        {["All", "≤30 days", "Remote", "Bangalore", "Design", "Backend"].map((f, i) => (
          <button key={f} className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap border ${i===0?"bg-[var(--ink)] text-[var(--paper)] border-[var(--ink)]":"bg-white border-[var(--line)]"}`}>{f}</button>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        {filtered.map(j => <JobCard key={j.id} job={j} onOpen={onOpenJob} saved={false} onSave={() => {}}/>)}
      </div>
    </div>
  );
}

function JobDetail({ job, onBack, onApply, saved, setSaved }) {
  if (!job) return null;
  const isSaved = saved.has(job.id);
  return (
    <div className="pb-6">
      <div className="px-5 pt-4 flex items-center justify-between">
        <button onClick={onBack} className="btn btn-ghost text-sm !px-3"><Icon.ChevronLeft/></button>
        <button onClick={() => {
          const n = new Set(saved); n.has(job.id) ? n.delete(job.id) : n.add(job.id); setSaved(n);
        }} className={isSaved ? "text-[var(--brand)]" : "text-[var(--muted)]"}>
          <Icon.Bookmark fill={isSaved ? "currentColor" : "none"}/>
        </button>
      </div>
      <div className="px-5 mt-2">
        <CompanyLogo name={job.company} size={56}/>
        <h1 className="font-display text-2xl mt-3 leading-tight">{job.title}</h1>
        <div className="text-sm text-[var(--muted)]">{job.company} · {job.postedAgo}</div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          <Chip><Icon.Location/> {job.location}</Chip>
          <Chip>{job.workMode}</Chip>
          <Chip><Icon.Money/> ₹{job.salaryMin}-{job.salaryMax} LPA</Chip>
          <Chip>{job.expMin}-{job.expMax} yrs</Chip>
          <Chip tone="accent"><Icon.Clock/> Max ≤{job.maxJoiningDays} days to join</Chip>
        </div>

        <div className="mt-5 p-3 rounded-xl bg-[var(--brand-3)]/40 flex items-center gap-3">
          <MatchRing score={job.match}/>
          <div>
            <div className="text-sm font-semibold">Strong match for your profile</div>
            <div className="text-xs text-[var(--brand)]">Availability aligned · 4/4 skills match</div>
          </div>
        </div>

        <h3 className="font-display text-lg mt-5">About the role</h3>
        <p className="text-sm text-[var(--ink-2)] mt-1 leading-relaxed">{job.description}</p>

        <h3 className="font-display text-lg mt-5">Required skills</h3>
        <div className="mt-2 flex flex-wrap gap-1.5">
          {job.skills.map(s => <Chip key={s} tone="mint">{s}</Chip>)}
        </div>
      </div>
      <div className="px-5 sticky bottom-0 pt-4 mt-4">
        <button data-testid="job-apply-btn" onClick={onApply} className="btn btn-primary w-full">Apply now</button>
      </div>
    </div>
  );
}

function ApplyScreen({ job, onDone, onBack }) {
  const [answers, setAnswers] = useState(job.questions.map(() => ""));
  return (
    <div className="px-5 pt-4">
      <button onClick={onBack} className="btn btn-ghost text-sm !px-3 mb-3"><Icon.ChevronLeft/> Back</button>
      <h1 className="font-display text-2xl">Screening questions</h1>
      <p className="text-sm text-[var(--muted)] mt-1">2 quick questions from AcmeCorp</p>
      <div className="mt-5 space-y-4">
        {job.questions.map((q, i) => (
          <div key={i}>
            <label className="text-sm font-medium">{i+1}. {q}</label>
            <textarea value={answers[i]} onChange={e => { const n=[...answers]; n[i]=e.target.value; setAnswers(n); }}
              rows={3} className="mt-2 w-full px-3 py-2 rounded-xl border border-[var(--line)] bg-white text-sm resize-none"
              placeholder="Your answer"/>
          </div>
        ))}
      </div>
      <div className="mt-6 p-3 rounded-xl bg-[var(--paper-2)] text-xs text-[var(--ink-2)]">
        <b>Attached:</b> arjun_mehta_cv.pdf · Profile snapshot
      </div>
      <button data-testid="apply-submit" onClick={onDone} className="btn btn-primary w-full mt-6">Submit application</button>
    </div>
  );
}

function ApplySuccess({ job, go, setTab }) {
  return (
    <div className="px-6 pt-16 h-full flex flex-col items-center text-center">
      <div className="w-20 h-20 rounded-full bg-[var(--brand-3)] grid place-items-center">
        <div className="w-14 h-14 rounded-full bg-[var(--brand-2)] grid place-items-center"><Icon.Check color="white"/></div>
      </div>
      <h1 className="font-display text-2xl mt-6">Application sent!</h1>
      <p className="text-sm text-[var(--muted)] mt-2">AcmeCorp will review and get back to you within 5 working days.</p>
      <div className="mt-6 w-full card p-4 text-left">
        <div className="font-semibold text-sm">{job.title}</div>
        <div className="text-xs text-[var(--muted)] mt-1">{job.company}</div>
      </div>
      <button data-testid="apply-track" onClick={() => { setTab("apps"); go("app"); }} className="btn btn-primary w-full mt-6">Track application</button>
      <button onClick={() => { setTab("home"); go("app"); }} className="btn btn-ghost w-full mt-2">Back to home</button>
    </div>
  );
}

function AppsTracker() {
  return (
    <div className="px-5 pt-4">
      <h1 className="font-display text-2xl">My applications</h1>
      <p className="text-sm text-[var(--muted)] mt-1">3 active · 1 offer pending</p>
      <div className="mt-4 space-y-3">
        {CANDIDATE_APPS.map((a, i) => {
          const job = JOBS.find(j => j.id === a.jobId);
          const idx = TRACKER_STAGES.indexOf(a.stage);
          return (
            <div key={i} className="card p-4">
              <div className="flex items-start gap-3">
                <CompanyLogo name={job.company} size={40}/>
                <div className="flex-1">
                  <div className="font-semibold text-sm">{job.title}</div>
                  <div className="text-xs text-[var(--muted)] mt-0.5">{job.company} · {a.appliedAgo}</div>
                </div>
                <Chip tone="mint">{a.stage}</Chip>
              </div>
              <div className="mt-4 grid grid-cols-6 gap-1">
                {TRACKER_STAGES.map((s, si) => (
                  <div key={s} className="flex flex-col items-center">
                    <div className={`w-full h-1 rounded ${si <= idx ? "bg-[var(--brand-2)]" : "bg-[var(--paper-2)]"}`}/>
                    <span className={`text-[9px] mt-1 ${si <= idx ? "text-[var(--brand)] font-semibold" : "text-[var(--muted)]"} capitalize`}>{s}</span>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ProfileScreen({ avail }) {
  return (
    <div className="pb-6">
      <div className="px-5 pt-4 flex items-center justify-between">
        <h1 className="font-display text-2xl">Profile</h1>
        <button><Icon.Settings/></button>
      </div>
      <div className="px-5 mt-4">
        <div className="flex items-center gap-3">
          <Avatar name="Arjun Mehta" size={64}/>
          <div>
            <div className="font-display text-lg">{me.name}</div>
            <div className="text-xs text-[var(--muted)]">{me.title} · {me.company}</div>
            <div className="mt-1"><AvailBadge status={avail.status} days={avail.status === "immediate" ? 0 : avail.days}/></div>
          </div>
        </div>

        <div className="mt-5 card p-4">
          <div className="flex items-center justify-between text-xs">
            <span className="text-[var(--muted)]">Profile completion</span>
            <span className="font-semibold text-[var(--brand)]">{me.completion}%</span>
          </div>
          <div className="mt-2"><Progress value={me.completion}/></div>
          <div className="mt-2 text-xs text-[var(--muted)]">You can apply to any role.</div>
        </div>

        <div className="mt-4 card p-4">
          <div className="font-display text-lg mb-2">About</div>
          <p className="text-sm text-[var(--ink-2)]">{me.bio}</p>
        </div>

        <div className="mt-4 card p-4">
          <div className="font-display text-lg mb-2">Skills</div>
          <div className="flex flex-wrap gap-1.5">{me.skills.map(s => <Chip key={s} tone="mint">{s}</Chip>)}</div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="card p-3">
            <div className="text-xs text-[var(--muted)]">Experience</div>
            <div className="font-display text-xl">{me.exp} yrs</div>
          </div>
          <div className="card p-3">
            <div className="text-xs text-[var(--muted)]">Expected</div>
            <div className="font-display text-xl">₹{me.salary}L</div>
          </div>
        </div>
      </div>
    </div>
  );
}
